/**
 * Admin header "Fulfilment Outstanding" alert badge — Feb 2026.
 *
 * Sits next to `AdminPresenceChip` in the admin portal header
 * (`PortalShell.jsx`) and polls `GET /api/admin/fulfilment/counts`
 * every 60 seconds. Surfaces the three actionable buckets so posted
 * kits never fall through the cracks:
 *
 *    Labels to create   — mail-out booking with no Starshipit order
 *    Kits to post       — label exists but kit not marked as sent
 *    Awaiting return    — kit posted, sample not yet received back
 *
 * Rendering rules:
 *   • Total = 0    → subtle green "All fulfilled" chip
 *   • Total 1-4    → amber pill with total count
 *   • Total 5+     → rose pill with total count + subtle pulse
 *   • Click        → jumps to /admin/kits/today (existing fulfilment page)
 *   • Popover      → per-bucket counts + direct action links
 *
 * Self-hides for non-admin users. Cancelled / archived / soft-deleted
 * bookings are excluded server-side by the central lifecycle filter,
 * so this badge only ever reflects active work.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PackageCheck, PackageSearch } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_INTERVAL_MS = 30_000;

const CUSTOMER_BUCKETS = [
  {
    key:   "labels_to_create",
    label: "Customer kit labels to create",
    hint:  "Mail-out booking is paid but no Starshipit label exists yet.",
  },
  {
    key:   "kit_to_post",
    label: "Customer kits to post",
    hint:  "Label ready but the kit hasn't been marked as posted yet.",
  },
  {
    key:   "awaiting_return",
    label: "Customer kits awaiting return",
    hint:  "Kit is in transit — sample not yet marked as received back.",
  },
];

const BULK_BUCKETS = [
  {
    key:   "labels_to_create",
    label: "PWS bulk labels to create",
    hint:  "Partner paid for a bulk order — outbound & return labels not yet generated.",
  },
  {
    key:   "ready_to_pack",
    label: "PWS bulk ready to pack/post",
    hint:  "Labels ready — boxes waiting to be packed & marked shipped.",
  },
];

export function FulfilmentOutstandingBadge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const popRef = useRef(null);

  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/fulfilment/counts");
        if (mounted) setData(data);
      } catch { /* silent — chip just stays stale */ } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const iv = setInterval(() => {
      // Skip polling in backgrounded tabs — saves a fulfilment-counts
      // scan per hidden admin tab every 30s.
      if (document.visibilityState === "visible") load();
    }, POLL_INTERVAL_MS);
    return () => { mounted = false; clearInterval(iv); };
  }, [isAdmin]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!isAdmin) return null;

  const total = data?.outstanding_total ?? 0;
  const overdueCount = data?.awaiting_return_overdue ?? 0;
  const allClear = !loading && total === 0;
  // Feb 2026 — treat "any overdue return" as high-severity even if
  // total is small; a single lost kit deserves the pulse.
  const highSeverity = total >= 5 || overdueCount > 0;

  const tone = allClear
    ? "bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
    : highSeverity
      ? "bg-rose-50 text-rose-800 border border-rose-300 hover:bg-rose-100"
      : "bg-amber-50 text-amber-900 border border-amber-300 hover:bg-amber-100";

  const dotTone = allClear
    ? "bg-emerald-500"
    : highSeverity
      ? "bg-rose-500 animate-pulse"
      : "bg-amber-500";

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="fulfilment-outstanding-badge"
        title={
          loading
            ? "Loading fulfilment status…"
            : allClear
              ? "No outstanding fulfilment tasks — well done."
              : `${total} outstanding · click for breakdown`
        }
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${tone}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dotTone}`} />
        {allClear ? (
          <>
            <PackageCheck className="w-3.5 h-3.5" />
            <span data-testid="fulfilment-badge-label">All fulfilled</span>
          </>
        ) : (
          <>
            <PackageSearch className="w-3.5 h-3.5" />
            <span data-testid="fulfilment-badge-label">Fulfilment</span>
            <span
              data-testid="fulfilment-badge-total"
              className={`ml-0.5 inline-flex min-w-[18px] justify-center rounded-full ${
                highSeverity ? "bg-rose-600 text-white" : "bg-amber-600 text-white"
              } px-1 text-[10px] leading-4`}
            >
              {loading ? "·" : total}
            </span>
          </>
        )}
      </button>

      {open && (
        <div
          data-testid="fulfilment-popover"
          className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-lg z-50"
        >
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-baseline justify-between">
              <div className="text-sm font-semibold text-slate-800">
                Fulfilment outstanding
              </div>
              <div className="text-[11px] text-slate-500">
                {loading ? "loading…" : allClear ? "all clear" : `${total} total`}
              </div>
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              Cancelled &amp; archived bookings are already excluded.
            </div>
          </div>
          <ul className="p-2">
            {CUSTOMER_BUCKETS.map((b) => {
              const n = data?.[b.key] ?? 0;
              const active = n > 0;
              // Feb 2026 (SCWT-52B41C follow-up) — flag the overdue
              // subset of `awaiting_return` (kits in transit for >14
              // days) so a lost kit surfaces in the badge instead of
              // sitting silently forever.
              const overdue = b.key === "awaiting_return"
                ? (data?.awaiting_return_overdue ?? 0) : 0;
              return (
                <li key={`c-${b.key}`}>
                  <button
                    type="button"
                    data-testid={`fulfilment-bucket-${b.key}`}
                    onClick={() => {
                      setOpen(false);
                      const routeKey = overdue > 0 && b.key === "awaiting_return"
                        ? "awaiting_return_overdue"   // deep-link to overdue view
                        : b.key;
                      navigate(`/admin/kits/today?bucket=${routeKey}`);
                    }}
                    className={`w-full flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                      active ? "hover:bg-amber-50" : "opacity-70 hover:bg-slate-50"
                    }`}
                  >
                    <div>
                      <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
                        {b.label}
                        {overdue > 0 && (
                          <span
                            data-testid="fulfilment-overdue-chip"
                            className="inline-flex items-center rounded-full bg-rose-100 text-rose-800 border border-rose-300 px-1.5 py-0.5 text-[10px] font-semibold"
                          >
                            {overdue} overdue &gt;14d
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                        {b.hint}
                      </div>
                    </div>
                    <div
                      data-testid={`fulfilment-bucket-count-${b.key}`}
                      className={`shrink-0 inline-flex min-w-[24px] justify-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                        active ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {n}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          {/* PWS Bulk Kit Orders section — Feb 2026 SCWT-52B41C */}
          <div className="px-4 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 border-t border-slate-100">
            PWS Bulk Kit Orders
          </div>
          <ul className="p-2 pt-1">
            {BULK_BUCKETS.map((b) => {
              const n = data?.bulk_kit?.[b.key] ?? 0;
              const active = n > 0;
              // Feb 2026 — if there's EXACTLY ONE ready-to-pack order,
              // deep-link straight to its packing screen. Server
              // exposes the id via `bulk_kit.ready_to_pack_single_order_id`.
              const singleOrderId = b.key === "ready_to_pack"
                ? data?.bulk_kit?.ready_to_pack_single_order_id
                : null;
              const target = singleOrderId
                ? `/admin/bulk-kit-orders/${singleOrderId}/packing`
                : `/admin/bulk-kit-orders?bucket=${b.key}`;
              return (
                <li key={`bk-${b.key}`}>
                  <button
                    type="button"
                    data-testid={`fulfilment-bulk-bucket-${b.key}`}
                    onClick={() => {
                      setOpen(false);
                      navigate(target);
                    }}
                    className={`w-full flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                      active ? "hover:bg-sky-50" : "opacity-70 hover:bg-slate-50"
                    }`}
                  >
                    <div>
                      <div className="text-[13px] font-semibold text-slate-800">
                        {b.label}
                      </div>
                      <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                        {b.hint}
                        {b.key === "ready_to_pack" && data?.bulk_kit?.boxes_ready > 0 ? (
                          <span className="block text-slate-600 font-medium mt-0.5">
                            {data.bulk_kit.boxes_ready} box{data.bulk_kit.boxes_ready === 1 ? "" : "es"} across these orders
                          </span>
                        ) : null}
                        {singleOrderId ? (
                          <span
                            data-testid="fulfilment-bulk-single-hint"
                            className="block text-sky-700 font-medium mt-0.5"
                          >
                            Jump straight to the packing screen →
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div
                      data-testid={`fulfilment-bulk-bucket-count-${b.key}`}
                      className={`shrink-0 inline-flex min-w-[24px] justify-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                        active ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {n}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="p-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
            <span>
              Received today:{" "}
              <span className="font-semibold text-slate-700">
                {data?.received_today ?? 0}
              </span>
            </span>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/admin/kits/today");
              }}
              className="font-semibold text-emerald-700 hover:text-emerald-800"
              data-testid="fulfilment-open-kits-today"
            >
              Open Kits Today →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
